"use client";

import React from "react";
import { Cloud, Wrench, Cpu, Lightbulb, Headset, LayoutDashboard } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const specializations = [{
  title: "ללא התחייבות ארוכת טווח",
  description: "שירות גמיש המאפשר לכם להפסיק או לשנות את הטיפול בכל רגע, ללא קנסות או חוזים כובלים המכבידים עליכם.",
  icon: Cloud
}, {
  title: "הזמנה ליום בודד",
  description: "זקוקים לעזרה נקודתית בלבד? ניתן להזמין מטפל סיעודי מקצועי גם למספר שעות בודדות או ליום אחד בלבד.",
  icon: Wrench
}, {
  title: "פתרון מהיום להיום",
  description: "מענה מיידי למצבים דחופים ובלתי צפויים. אנו מתחייבים למציאת פתרון סיעודי איכותי בתוך שעות ספורות.",
  icon: Cpu
}, {
  title: "התאמה מהירה ומקצועית",
  description: "תהליך סינון קפדני ומהיר המבטיח התאמה מדויקת בין צרכי המטופל לבין ניסיון ואישיות המטפל הנבחר.",
  icon: Lightbulb
}, {
  title: "זמינות וליווי 24/7",
  description: "המוקד המקצועי שלנו זמין עבורכם מסביב לשעון לכל שאלה, עדכון או צורך דחוף שעולה מהשטח בזמן אמת.",
  icon: Headset
}, {
  title: "צוות מטפלים מוסמך",
  description: "כל המטפלים בצוות שלנו עוברים הכשרות מקצועיות שוטפות ובדיקות רקע מקיפות לשקט הנפשי המלא שלכם.",
  icon: LayoutDashboard
}];
const faqItems = [{
  id: "faq-1",
  question: "תוך כמה זמן המטפל מגיע?",
  answer: "אנו מתחייבים להגעת מטפל סיעודי מקצועי בתוך עד 5 שעות מרגע ההזמנה, בכל רחבי הארץ."
}, {
  id: "faq-2",
  question: "האם יש התחייבות לתקופה ארוכה?",
  answer: "לא, השירות שלנו גמיש לחלוטין. ניתן להזמין מטפל ליום בודד, למספר שעות או לתקופה ממושכת, ללא חוזים כובלים."
}, {
  id: "faq-3",
  question: "האם המטפלים שלכם הם ישראלים?",
  answer: "כן, אנו עובדים עם מטפלים ומטפלות ישראלים בלבד, דוברי עברית, שעברו תהליך סינון קפדני ובדיקות רקע."
}, {
  id: "faq-4",
  question: "האם אתם מספקים שירות גם בבתי חולים?",
  answer: "בוודאי. אנו מספקים שירותי השגחה וליווי צמוד גם בבתי חולים וגם בבית המטופל, בהתאם לצורך האישי שלכם."
}];
function AnimatedCard({
  item,
  index
}: {
  item: typeof specializations[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div data-section-id="160020" ref={ref} data-index={index} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl shadow-none p-0 flex flex-col items-center text-center h-full group hover:scale-[1.02] hover:shadow-[0_0_30px_-5px_rgba(34,211,238,0.3)] transition-all duration-300">
        <CardHeader className="p-0 flex flex-col items-center gap-5 mb-4">
          <div className="p-5 rounded-2xl bg-cyan-500/10 ring-1 ring-white/20 group-hover:bg-cyan-500/20 group-hover:scale-110 transition-all duration-300">
            <item.icon className="w-8 h-8 text-cyan-400" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-xl md:text-2xl font-default font-bold text-white tracking-tight">
            {item.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <p className="text-slate-300 font-default leading-relaxed text-sm md:text-base max-w-[280px] mx-auto">
            {item.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}
export default function SpecialExpertise() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="התמודדות-עם-התנגדויות" className="dark bg-background text-foreground py-20 md:py-28 px-6 md:px-8 overflow-hidden relative" dir="rtl">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-white mb-4">
            התמודדות עם התנגדויות
          </h2>
          <div className="w-24 h-1 bg-cyan-500 mx-auto rounded-full opacity-80" />
        </div>

        <div className="max-w-3xl mx-auto mb-20 space-y-12 text-right">
          <div className="space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-white">מבוא לשירות</h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              ב-Siudy, אנו מבינים שטיפול סיעודי אינו רק עניין של נוחות, אלא צורך חיוני שמתעורר לעיתים קרובות ללא התראה מוקדמת. המערך שלנו נבנה כדי לספק לכם שקט נפשי ברגעים המאתגרים ביותר, עם דגש על מהירות, אמינות ואנושיות.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-white">הפתרון</h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              אנו מציעים פלטפורמה המקשרת ביניכם לבין מטפלים ישראלים מיומנים בתוך פחות מ-5 שעות. הפתרון שלנו חוסך לכם את הבירוקרטיה המתישה ואת זמני ההמתנה הארוכים המאפיינים את המערכות המסורתיות, ומאפשר לכם לקבל עזרה כאן ועכשיו.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-white">יתרונות</h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              היתרון המרכזי שלנו הוא השילוב בין מהירות שיא לבין איכות בלתי מתפשרת. המטפלים שלנו הם דוברי עברית, בעלי ניסיון עשיר, וזמינים לעבודה בבית או בבית החולים. בנוסף, הגמישות המלאה שלנו מאפשרת לכם להפסיק את השירות בכל עת, ללא קנסות.
            </p>
          </div>
          <div className="space-y-4">
            <h3 className="text-2xl md:text-3xl font-bold text-white">מידע מפורט</h3>
            <p className="text-slate-300 text-lg leading-relaxed">
              תהליך העבודה אצלנו פשוט ושקוף: אתם יוצרים קשר, אנו מאפיינים את הצורך המדויק שלכם, ותוך שעות ספורות המטפל המתאים ביותר כבר נמצא אצלכם. אנו פועלים בפריסה ארצית מלאה ומלווים אתכם לאורך כל הדרך עם מוקד זמין 24/7 לכל שאלה או עדכון.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16">
          {specializations.map((item, index) => <AnimatedCard key={index} item={item} index={index} />)}
        </div>

        <div className="mt-28 max-w-3xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">שאלות נפוצות</h3>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border-white/10">
                <AccordionTrigger className="text-white hover:text-cyan-400 text-right text-lg py-6 transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-300 text-right text-base leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <div className="mt-20 flex flex-wrap justify-center gap-6">
          <Button asChild size="lg" className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-slate-900 px-10 py-7 text-lg font-bold shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-all duration-300 hover:scale-105">
            <Link to="#">התקשרו עכשיו - מענה מיידי</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-full border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 px-10 py-7 text-lg font-bold transition-all duration-300 hover:scale-105">
            <Link to="https://wa.me/972500000000" newTab>וואטסאפ מיידי</Link>
          </Button>
        </div>
      </div>
    </section>;
}